import { CellCoord } from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import {
    MetadataColumnExtractorId,
    MetadataColumnWithId,
} from "org/risks/content/types";

import { Codecs, PageFilterAndColumns } from "../../types";
import { unwrapPageStateAs } from "../../utils";

import {
    computeIdToNameMaps,
    decodeRiskMapColumn,
    decodeRiskMapPageFilters,
    encodeRiskMapColumn,
    encodeRiskMapFilters,
} from "./common";

export function createDecodeRiskMapPageStateFn(
    customColumns: MetadataColumnWithId<MetadataColumnExtractorId>[]
): (pageState: RiskMapPageStateWithType) => RiskMapPageStateRaw {
    const [mapIdToName, mapOptionIdToName] = computeIdToNameMaps(customColumns);
    return (pageState: RiskMapPageStateWithType): RiskMapPageStateRaw => {
        const unwrapped = unwrapPageStateAs(pageState, "RiskMap");
        const {
            filtersAndColumns,
            hideHeatmap,
            showResidualHeatmap,
            showTableView,
            focusFilterCoords,
        } = unwrapped;
        const filter = decodeRiskMapPageFilters(
            mapIdToName,
            mapOptionIdToName,
            filtersAndColumns.filters,
            "RiskMap"
        );
        const visibleColumnIds = new Set(
            filtersAndColumns.columns.map((col) =>
                decodeRiskMapColumn(mapIdToName, col)
            )
        );
        return {
            pageType: pageState.type,
            filter,
            visibleColumnIds,
            hideHeatmap,
            showResidualHeatmap,
            showTableView,
            focusFilterCoords,
            version: pageState.filtersAndColumns.version ?? 0,
        };
    };
}

function encodeRiskMapPageState(
    raw: RiskMapPageStateRaw
): RiskMapPageStateWithType {
    const {
        filter,
        visibleColumnIds,
        hideHeatmap,
        showResidualHeatmap,
        showTableView,
        focusFilterCoords,
    } = raw;
    return {
        type: "RiskMap",
        filtersAndColumns: {
            filters: encodeRiskMapFilters("RiskMap", filter),
            columns: Array.from(visibleColumnIds).map(encodeRiskMapColumn),
            version: raw.version,
        },
        hideHeatmap,
        showResidualHeatmap,
        showTableView,
        focusFilterCoords,
    };
}

export interface RiskMapPageStateRaw extends PageFilterAndColumns {
    hideHeatmap: boolean;
    showResidualHeatmap: boolean;
    showTableView: boolean;
    focusFilterCoords: CellCoord[];
}

export type RiskMapPageStateWithType = PageStateData<"RiskMap"> & {
    type: "RiskMap";
};

export function pageStateRiskMapsCodecs(
    customColumns: MetadataColumnWithId<MetadataColumnExtractorId>[]
): Codecs<RiskMapPageStateRaw, RiskMapPageStateWithType> {
    return {
        encode: encodeRiskMapPageState,
        decode: createDecodeRiskMapPageStateFn(customColumns),
    };
}
